import { arcgisSearch } from './arcgisSearch';
import React from 'react';

/**
 * Návrhy miest z ArcGISu pre panel hľadania. Prázdny `term` znamená ticho -
 * žiadny dotaz a prázdny zoznam.
 */
export const usePlaceSuggestions = (
  term: string,
  view?: __esri.MapView | null,
) => {
  const [results, setResults] = React.useState<__esri.SuggestResult[]>([]);

  /**
   * View model sa vyrába dopredu, nie až pri prvom písmene.
   *
   * Jeho `defaultSources` totiž hneď po vytvorení ešte prázdne, a práve na ne
   * sa vešia obmedzenie na Slovensko. Bez tohto rozohriatia išla **prvá**
   * otázka nezúžená a na „chodnik" ponúkala poľské obce; druhá už bola
   * v poriadku, čo je presne stopa nedokončenej inicializácie.
   */
  React.useEffect(() => {
    void arcgisSearch(view).catch(() => undefined);
  }, [view]);

  React.useEffect(() => {
    let cancelled = false;

    if (!term) {
      setResults([]);
      return;
    }

    const run = async () => {
      try {
        const model = await arcgisSearch(view);
        const response = await model.suggest(term);
        if (cancelled) return;
        setResults(
          (response?.results ?? []).flatMap((group) => group.results ?? []),
        );
      } catch {
        // Zlyhané hľadanie miesta nie je chyba appky - podnety a plus kód
        // v zozname zostávajú a panel má čo ukázať.
        if (!cancelled) setResults([]);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [term, view]);

  /** Zo zvoleného návrhu spraví súradnice `[lng, lat]`. */
  const resolve = React.useCallback(
    async (
      suggestion: __esri.SuggestResult,
    ): Promise<[number, number] | undefined> => {
      const model = await arcgisSearch(view);
      const response = await model.search(suggestion);
      const result = response?.results?.[0]?.results?.[0];
      const point = (result?.feature?.geometry ??
        result?.extent?.center) as __esri.Point | undefined;

      return point ? [point.longitude, point.latitude] : undefined;
    },
    [view],
  );

  return { results, resolve };
};
